/**
 * Requirement expressions for STRUCTURED questions (Phase 1C).
 *
 * A STRUCTURED question's `parts[]` can be combined with AND / OR / "K-of-N"
 * logic to express real exam shapes the flat per-part scheme could not:
 *
 *   - either-or            a OR b                  → answer one of a, b
 *   - K of N               ANY 2 OF (a, b, c)      → answer any two
 *   - nested choice        (a AND b) OR (c AND d)  → do one *pair*
 *   - mixed                (a OR b) AND (c OR d)
 *
 * The logic is stored as a small recursive tree on `Question.requirement`
 * (absent ⇒ every part required, i.e. an implicit `all`). The tree is the
 * single source of truth; the admin Custom expression string compiles **to**
 * it and the preview string serializes **from** it.
 *
 * Three node kinds:
 *   - part:  a leaf — one `partId`.
 *   - all:   AND — every child required.
 *   - any:   OR / choose-K — `choose` of the children required (`choose:1` = OR).
 *
 * `any` folds OR and "attempt K of N" into one node, and the same node type is
 * reused at the section level in Phase 2 (`evaluateRequirement` over question
 * results instead of part results).
 *
 * See docs/FeatureDesigns/question-structure.md §Phase 1C.
 */

export type PartRequirement =
    | { kind: 'part'; partId: string }
    | { kind: 'all'; of: PartRequirement[] }
    | { kind: 'any'; of: PartRequirement[]; choose: number };

// ---------------------------------------------------------------------------
// Builders (used by the admin "All parts" / "Any N of all parts" presets)
// ---------------------------------------------------------------------------

/** AND over the given parts. Single id collapses to a bare leaf. */
export function allOfParts(partIds: readonly string[]): PartRequirement {
    const leaves = partIds.map((partId): PartRequirement => ({ kind: 'part', partId }));
    return leaves.length === 1 ? leaves[0] : { kind: 'all', of: leaves };
}

/** "Any `choose` of the given parts." `choose:1` is a plain OR. */
export function anyOfParts(partIds: readonly string[], choose: number): PartRequirement {
    return { kind: 'any', of: partIds.map(partId => ({ kind: 'part', partId })), choose };
}

// ---------------------------------------------------------------------------
// Tree walks
// ---------------------------------------------------------------------------

/** All leaf partIds referenced by the tree, in left-to-right order (with repeats). */
export function collectPartIds(req: PartRequirement): string[] {
    if (req.kind === 'part') return [req.partId];
    return req.of.flatMap(collectPartIds);
}

/**
 * Validate a requirement tree against the question's declared parts.
 * Returns a list of human-readable problems (empty ⇒ valid). Enforces:
 *   - every leaf references a real part,
 *   - every part is used exactly once (an unused part can never be scored;
 *     a duplicated part is ambiguous),
 *   - `1 ≤ choose ≤ children` on every `any`, and non-empty groups.
 */
export function validateRequirement(req: PartRequirement, partIds: readonly string[]): string[] {
    const errors: string[] = [];
    const known = new Set(partIds);
    const seen = new Map<string, number>();

    const walk = (node: PartRequirement): void => {
        if (node.kind === 'part') {
            if (!known.has(node.partId)) errors.push(`Unknown part: ${node.partId}`);
            seen.set(node.partId, (seen.get(node.partId) ?? 0) + 1);
            return;
        }
        if (node.of.length === 0) {
            errors.push(`Empty ${node.kind.toUpperCase()} group`);
        }
        if (node.kind === 'any') {
            if (!Number.isInteger(node.choose) || node.choose < 1) {
                errors.push(`"choose" must be ≥ 1 (got ${node.choose})`);
            } else if (node.choose > node.of.length) {
                errors.push(`"choose" ${node.choose} exceeds the ${node.of.length} option(s) in the group`);
            }
        }
        node.of.forEach(walk);
    };
    walk(req);

    for (const id of partIds) {
        const count = seen.get(id) ?? 0;
        if (count === 0) errors.push(`Part ${id} is never used in the requirement`);
        else if (count > 1) errors.push(`Part ${id} is used more than once`);
    }
    return errors;
}

// ---------------------------------------------------------------------------
// Evaluation (scoring) — pick the student's best satisfying branch
// ---------------------------------------------------------------------------

export interface RequirementEvaluation {
    /** The parts that count toward the score — the best satisfying branch.
     *  Parts NOT in this set are choice-group "losers": stored attempted:false,
     *  marksAwarded:0, and excluded from the question's max marks. */
    selectedPartIds: string[];
}

interface PartInfo {
    /** marks the student earned on this part (0 if unattempted/unscored). */
    marksAwarded: number;
    /** the part's max marks — used only for deterministic tie-breaks. */
    marks: number;
}

interface Branch {
    selected: string[];
    awarded: number;
    /** structural marks of the selected parts — tie-break key. */
    structural: number;
}

function bestBranch(node: PartRequirement, info: (partId: string) => PartInfo): Branch {
    if (node.kind === 'part') {
        const i = info(node.partId);
        return { selected: [node.partId], awarded: i.marksAwarded, structural: i.marks };
    }
    const children = node.of.map(c => bestBranch(c, info));
    if (node.kind === 'all') {
        return {
            selected: children.flatMap(c => c.selected),
            awarded: children.reduce((s, c) => s + c.awarded, 0),
            structural: children.reduce((s, c) => s + c.structural, 0),
        };
    }
    // any: take the `choose` best children. Rank by student marks earned, then
    // by structural marks, then original order — deterministic for ties (incl.
    // the all-unattempted case, where the richer option becomes the denominator).
    const ranked = children
        .map((c, idx) => ({ c, idx }))
        .sort((x, y) =>
            (y.c.awarded - x.c.awarded) ||
            (y.c.structural - x.c.structural) ||
            (x.idx - y.idx))
        .slice(0, Math.max(1, node.choose))
        .map(r => r.c);
    return {
        selected: ranked.flatMap(c => c.selected),
        awarded: ranked.reduce((s, c) => s + c.awarded, 0),
        structural: ranked.reduce((s, c) => s + c.structural, 0),
    };
}

/**
 * Resolve which parts count for a structured question given how the student
 * did on each. `marksAwardedByPart` / `marksByPart` are keyed by partId; a
 * missing entry is treated as 0 (unattempted). The caller sums marksAwarded
 * and marks over `selectedPartIds` to get the question's score and max marks.
 */
export function evaluateRequirement(
    req: PartRequirement,
    marksAwardedByPart: Readonly<Record<string, number>>,
    marksByPart: Readonly<Record<string, number>> = {},
): RequirementEvaluation {
    const info = (partId: string): PartInfo => ({
        marksAwarded: marksAwardedByPart[partId] ?? 0,
        marks: marksByPart[partId] ?? 0,
    });
    return { selectedPartIds: bestBranch(req, info).selected };
}

// ---------------------------------------------------------------------------
// Classify: tree → a flat shape the student UI can render (Phase 1C)
// ---------------------------------------------------------------------------

/**
 * A render-friendly summary of a requirement for the student app. The deep
 * tree is collapsed into the common shapes the UI hard-codes; anything else
 * falls back to the raw expression string.
 *   - all:     every part required (or no requirement) — render parts as-is.
 *   - choice:  pick `choose` of `options`; each option is one or more partIds
 *              that travel together (a leaf → 1 part; an AND-group → several).
 *              Covers `a OR b`, K-of-N, and `(a AND b) OR (c AND d)`.
 *   - complex: anything deeper/mixed (e.g. `(a OR b) AND (c OR d)`) — show
 *              `expression` as the instruction and render parts flat.
 */
export type RequirementShape =
    | { kind: 'all' }
    | { kind: 'choice'; choose: number; options: string[][] }
    | { kind: 'complex'; expression: string };

export function classifyRequirement(
    req: PartRequirement | undefined,
    _partIds?: readonly string[],
): RequirementShape {
    if (!req) return { kind: 'all' };
    if (req.kind === 'part') return { kind: 'all' };
    if (req.kind === 'all') {
        // Every part required iff all children are bare leaves; a nested choice
        // under the AND is not a simple "answer everything" shape.
        return req.of.every(c => c.kind === 'part')
            ? { kind: 'all' }
            : { kind: 'complex', expression: requirementToExpression(req) };
    }
    // any: each option must be a leaf or a flat AND-of-leaves to render simply.
    const options: string[][] = [];
    for (const child of req.of) {
        if (child.kind === 'part') {
            options.push([child.partId]);
        } else if (child.kind === 'all' && child.of.every(c => c.kind === 'part')) {
            options.push(child.of.map(c => (c as { partId: string }).partId));
        } else {
            return { kind: 'complex', expression: requirementToExpression(req) };
        }
    }
    return { kind: 'choice', choose: req.choose, options };
}

// ---------------------------------------------------------------------------
// Serialize: tree → expression string (for the admin preview / round-trip)
// ---------------------------------------------------------------------------

/** Render the tree as a canonical expression, e.g. `(a AND b) OR (c AND d)`. */
export function requirementToExpression(req: PartRequirement): string {
    const render = (node: PartRequirement, parentPrecedence: number): string => {
        if (node.kind === 'part') return node.partId;
        if (node.kind === 'all') {
            const inner = node.of.map(c => render(c, 2)).join(' AND ');
            // Parenthesize an AND-group whenever it's nested (not the root) so
            // the preview matches the explicit `(a AND b) OR (c AND d)` form.
            return parentPrecedence >= 1 ? `(${inner})` : inner;
        }
        // any
        if (node.choose === 1) {
            const inner = node.of.map(c => render(c, 1)).join(' OR ');
            return parentPrecedence > 0 ? `(${inner})` : inner;
        }
        // K-of-N: only valid over leaves in the grammar; render as ANY k OF (...)
        const ids = node.of.map(c => (c.kind === 'part' ? c.partId : render(c, 0)));
        return `ANY ${node.choose} OF (${ids.join(', ')})`;
    };
    return render(req, 0);
}

// ---------------------------------------------------------------------------
// Parse: Custom expression string → tree
// ---------------------------------------------------------------------------

export type ParseResult =
    | { ok: true; requirement: PartRequirement }
    | { ok: false; error: string };

type Token =
    | { t: 'id'; v: string }
    | { t: 'num'; v: number }
    | { t: 'kw'; v: 'AND' | 'OR' | 'ANY' | 'OF' }
    | { t: '(' } | { t: ')' } | { t: ',' };

const KEYWORDS = new Set(['AND', 'OR', 'ANY', 'OF']);

function tokenize(input: string): Token[] | string {
    const tokens: Token[] = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if (/\s/.test(ch)) { i++; continue; }
        if (ch === '(') { tokens.push({ t: '(' }); i++; continue; }
        if (ch === ')') { tokens.push({ t: ')' }); i++; continue; }
        if (ch === ',') { tokens.push({ t: ',' }); i++; continue; }
        if (/[0-9]/.test(ch)) {
            let j = i;
            while (j < input.length && /[0-9]/.test(input[j])) j++;
            tokens.push({ t: 'num', v: parseInt(input.slice(i, j), 10) });
            i = j;
            continue;
        }
        if (/[A-Za-z]/.test(ch)) {
            let j = i;
            while (j < input.length && /[A-Za-z0-9_.]/.test(input[j])) j++;
            const word = input.slice(i, j);
            const upper = word.toUpperCase();
            if (KEYWORDS.has(upper)) tokens.push({ t: 'kw', v: upper as 'AND' | 'OR' | 'ANY' | 'OF' });
            else tokens.push({ t: 'id', v: word });
            i = j;
            continue;
        }
        return `Unexpected character "${ch}"`;
    }
    return tokens;
}

/**
 * Parse a Custom requirement expression against the question's part ids.
 * Validates syntax, that every id exists, K ranges, and (via
 * validateRequirement) that every part is used exactly once.
 */
export function parseRequirementExpression(expr: string, partIds: readonly string[]): ParseResult {
    const tokens = tokenize(expr);
    if (typeof tokens === 'string') return { ok: false, error: tokens };
    if (tokens.length === 0) return { ok: false, error: 'Expression is empty' };

    let pos = 0;
    const peek = (): Token | undefined => tokens[pos];
    const fail = (msg: string): never => { throw new Error(msg); };

    const parseExpr = (): PartRequirement => parseOr();

    const parseOr = (): PartRequirement => {
        const operands = [parseAnd()];
        while (peek()?.t === 'kw' && (peek() as { v: string }).v === 'OR') {
            pos++;
            operands.push(parseAnd());
        }
        return operands.length === 1 ? operands[0] : { kind: 'any', of: operands, choose: 1 };
    };

    const parseAnd = (): PartRequirement => {
        const operands = [parseAtom()];
        while (peek()?.t === 'kw' && (peek() as { v: string }).v === 'AND') {
            pos++;
            operands.push(parseAtom());
        }
        return operands.length === 1 ? operands[0] : { kind: 'all', of: operands };
    };

    const parseAtom = (): PartRequirement => {
        const tok = peek();
        if (!tok) fail('Unexpected end of expression');
        if (tok!.t === '(') {
            pos++;
            const inner = parseExpr();
            if (peek()?.t !== ')') fail('Missing closing parenthesis');
            pos++;
            return inner;
        }
        if (tok!.t === 'kw' && tok!.v === 'ANY') {
            pos++;
            const num = peek();
            if (num?.t !== 'num') fail('Expected a number after ANY (e.g. "ANY 2 OF (a, b, c)")');
            pos++;
            if (!(peek()?.t === 'kw' && (peek() as { v: string }).v === 'OF')) fail('Expected OF after "ANY <n>"');
            pos++;
            if (peek()?.t !== '(') fail('Expected "(" after "ANY <n> OF"');
            pos++;
            const ids: PartRequirement[] = [];
            for (;;) {
                const idt = peek();
                if (idt?.t !== 'id') fail('Expected a part id inside ANY (...)');
                ids.push({ kind: 'part', partId: (idt as { v: string }).v });
                pos++;
                const sep = peek();
                if (sep?.t === ',') { pos++; continue; }
                if (sep?.t === ')') { pos++; break; }
                fail('Expected "," or ")" inside ANY (...)');
            }
            return { kind: 'any', of: ids, choose: (num as { v: number }).v };
        }
        if (tok!.t === 'id') {
            pos++;
            return { kind: 'part', partId: tok!.v };
        }
        return fail(`Unexpected token "${describeToken(tok!)}"`);
    };

    let tree: PartRequirement;
    try {
        tree = parseExpr();
    } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
    if (pos !== tokens.length) {
        return { ok: false, error: `Unexpected "${describeToken(tokens[pos])}" — check operators and parentheses` };
    }

    const problems = validateRequirement(tree, partIds);
    if (problems.length > 0) return { ok: false, error: problems[0] };
    return { ok: true, requirement: tree };
}

function describeToken(tok: Token | undefined): string {
    if (!tok) return 'end of expression';
    if (tok.t === 'id') return tok.v;
    if (tok.t === 'num') return String(tok.v);
    if (tok.t === 'kw') return tok.v;
    return tok.t;
}
